/**
 * Search page — the admin surface for what the app's Search tab shows.
 *
 * Evan (2026-08-23): "need Search Page tab in admin dash for the new collections stuff."
 *
 * Four sections, and the split is the point:
 *
 *   Spotlight     ⚠️ THE FIRST PILL AND THE DEFAULT SECTION (Evan, 2026-09-19: *"I don't see where
 *                 I can mess with the spotlight from my admin dashboard. It should be on the search
 *                 page tab."*). The one slot on the Search tab whose CONTENT Evan writes rather
 *                 than curates — announcements, polls and free-text questions, all typed by hand
 *                 (`components/SpotlightPanel.tsx`). It is here and not in its own sidebar entry,
 *                 and explicitly NOT folded into Announcements, because it is a thing ON the search
 *                 page and this screen is "what the app's Search tab shows". Opening Search page
 *                 lands on it: it is the only section here that is a WRITING surface, so it is the
 *                 one with a reason to be open before you have clicked anything.
 *
 *                 It is also the MODERATION surface (Evan, 2026-09-22: *"we should have an admin
 *                 switch for it and detailed management of it from admin dash. under search page."*
 *                 — "it" being the message board under the Tonight poll). The two per-card switches
 *                 live in the panel's table and the board itself in
 *                 `components/SpotlightBoardPanel.tsx`. ⚠️ THAT MAKES THIS SCREEN THE ONLY ROUTE TO
 *                 A TAKEDOWN, so it is not somewhere a section can be quietly demoted behind a
 *                 second click.
 *   Collections   the CONTROL surface — every collection, live/off, rename, cover, and one click
 *                 into any single collection (`components/CollectionDetailPanel.tsx`: the venue
 *                 list with its evidence, pin / exclude, rule editing with a live count, per-city
 *                 coverage). This renders the existing `CollectionsScreen` unchanged rather than a
 *                 second copy of it. There is exactly one implementation of "flip a collection
 *                 live", and it is the one that already goes through the `admin_*` RPCs.
 *   Live preview  the RESULT — `get_search_guides`, the same RPC `lib/venueSearch.ts` calls,
 *                 executed for one city. What a reader actually receives, including which live
 *                 collections silently do not make it.
 *
 * ─── WHY A PREVIEW IS NOT REDUNDANT WITH THE TABLE ───────────────────────────────────────────
 * "Live" in `search_guides` is not "on the page". `get_search_guides` drops any collection that
 * cannot fill `p_min_venues` (4) in the reader's city — deliberately, because an empty rail reads
 * as a broken app. Measured on production 2026-08-24: 38 collections exist and 18 are `is_active`,
 * and across the 28 cities with a previewable pool, "Best patios" clears 4 venues in ZERO of them
 * and "Where people are dancing" in one. A table alone cannot tell you that, and the toggle you
 * would reach for is not the problem. The Cities tab inside a collection is the per-collection
 * version of this same measurement.
 *
 * ─── ⚠️ ONE COLLECTION IS VIEWER-DEPENDENT ───────────────────────────────────────────────────
 * "Popular with friends" (`kind = 'friends'`) resolves through `search_guide_venue_ids`, which
 * reads `auth.uid()`. It therefore has no answer that is true for everybody: run from a
 * service-role or admin context it returns the ADMIN's friends, or nothing at all. Absent from
 * the preview does NOT mean off and does NOT mean empty. Both sections say so in place instead of
 * rendering a zero — `VIEWER_KINDS` below, and the callout under the preview table.
 *
 * ─── DEGRADES, DOES NOT ERROR ────────────────────────────────────────────────────────────────
 * `supabase/migrations/20260871000000_collection_admin_covers.sql` IS NOW APPLIED (verified
 * 2026-08-24), so the city-aware `admin_list_search_guides(p_city_id)`, the cover RPCs and
 * `admin_guide_preview_cities()` are live. The fallback to the no-argument
 * `admin_list_search_guides()` is kept anyway: it costs nothing and it is what turns the NEXT
 * unapplied migration into a quiet set-up note instead of a red banner. A missing function arrives
 * from PostgREST as PGRST202 / SQLSTATE 42883 — matched by `rpcMissing` in `lib/collectionKinds`.
 * Same shape as the `CityOverviews` set-up note.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from '../lib/useToast';
import { PageHeader } from '../ui/PageHeader';
import { ErrorBanner } from '../ui/ErrorBanner';
import { EmptyState } from '../ui/EmptyState';
import { StatCard } from '../ui/StatCard';
import { SkeletonRows } from '../ui/Skeleton';
import { CollectionsScreen } from './CollectionsScreen';
import { CollectionAssistantPanel } from '../components/CollectionAssistantPanel';
import { SpotlightPanel } from '../components/SpotlightPanel';
import {
  KIND_LABELS,
  MIN_VENUES,
  VIEWER_KINDS,
  errText,
  rpcMissing,
} from '../lib/collectionKinds';
import {
  loadPreviewCities,
  readStoredPreviewCity,
  writeStoredPreviewCity,
  type PreviewCity,
} from '../lib/guidePreviewCities';

type Section = 'collections' | 'preview' | 'assistant' | 'spotlight';

/**
 * ⚠️ THE KEY IS VERSIONED, AND BUMPING IT IS THE POINT. The default section moved from
 * `collections` to `spotlight` (Evan could not find the spotlight at all), and every browser that
 * has ever opened this screen already has `collections` written under the old key — which would
 * have beaten the new default forever, for exactly the one person the change is for. A new key
 * starts everybody on the spotlight once; their next click is remembered as before.
 */
const SECTION_STORAGE_KEY = 'admin:searchPage:section:v2';

/**
 * Matches `lib/venueSearch.ts` — previewing with different numbers would preview a different page.
 * `MIN_VENUES` and the kind vocabulary come from `lib/collectionKinds`: this screen had its own
 * copy, and that copy did not know about `underground` or `neighborhood`, so three live rows
 * rendered as raw slugs.
 */
const PER_GUIDE = 10;

/** One venue as `get_search_guides` returns it. */
type PreviewVenue = {
  venue_id: string;
  name: string | null;
  score: number | null;
  /** Repo rule: a DISPLAYED count is distinct raters (`venue_shado_unique_users`). */
  rater_count: number | null;
  photo_url: string | null;
};

type PreviewGuide = {
  id: string;
  title: string;
  subtitle: string | null;
  kind: string;
  venues: PreviewVenue[];
};

/** The subset of `admin_list_search_guides` the cross-reference needs; both signatures return it. */
type TableGuide = { id: string; title: string; kind: string; is_active: boolean; venue_count: number };

function readStoredSection(): Section {
  try {
    const v = window.localStorage.getItem(SECTION_STORAGE_KEY);
    return v === 'preview' || v === 'assistant' || v === 'collections' ? v : 'spotlight';
  } catch {
    return 'spotlight';
  }
}

function writeStoredSection(s: Section): void {
  try {
    window.localStorage.setItem(SECTION_STORAGE_KEY, s);
  } catch {
    /* private window / blocked site data — the strip still works for this session */
  }
}

function normalizePreview(raw: unknown): { city: string | null; guides: PreviewGuide[] } {
  if (!raw || typeof raw !== 'object') return { city: null, guides: [] };
  const o = raw as { city?: unknown; guides?: unknown };
  const guides = Array.isArray(o.guides) ? (o.guides as Partial<PreviewGuide>[]) : [];
  return {
    city: typeof o.city === 'string' ? o.city : null,
    guides: guides.map((g) => ({
      id: String(g.id ?? ''),
      title: g.title ?? '',
      subtitle: g.subtitle ? String(g.subtitle) : null,
      kind: g.kind ?? 'manual',
      venues: Array.isArray(g.venues) ? (g.venues as PreviewVenue[]) : [],
    })),
  };
}

function normalizeTable(raw: unknown): TableGuide[] {
  if (!Array.isArray(raw)) return [];
  return (raw as Partial<TableGuide>[]).map((g) => ({
    id: String(g.id ?? ''),
    title: g.title ?? '',
    kind: g.kind ?? 'manual',
    is_active: Boolean(g.is_active),
    venue_count: typeof g.venue_count === 'number' ? g.venue_count : Number(g.venue_count ?? 0) || 0,
  }));
}

/**
 * What a reader in one city gets back right now.
 *
 * Everything here is READ-ONLY on purpose: the switches live one pill to the left, and a preview
 * that can also mutate invites changing the thing you are measuring while you measure it.
 */
function LivePreviewSection() {
  const [cities, setCities] = useState<PreviewCity[]>([]);
  const [cityId, setCityId] = useState<string>(readStoredPreviewCity);
  const [resolvedCity, setResolvedCity] = useState<string | null>(null);
  const [guides, setGuides] = useState<PreviewGuide[]>([]);
  const [table, setTable] = useState<TableGuide[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [setupNote, setSetupNote] = useState<string | null>(null);
  const { showError, toastNode } = useToast();

  /**
   * `city` is a PARAMETER, not captured state: `run` is memoised so the Refresh button keeps its
   * identity, and a closure over `cityId` would pin it to whatever was selected at first render.
   */
  const run = useCallback(async (city: PreviewCity | null) => {
    setLoading(true);
    setError(null);
    try {
      const preview = await supabase.rpc('get_search_guides', {
        p_lat: city?.lat ?? null,
        p_lng: city?.lng ?? null,
        p_per_guide: PER_GUIDE,
        p_min_venues: MIN_VENUES,
      });
      if (preview.error) throw new Error(preview.error.message);
      const normalized = normalizePreview(preview.data);
      setGuides(normalized.guides);
      setResolvedCity(normalized.city);

      /* Cross-reference. City-aware signature first; the live one takes no arguments. */
      let note: string | null = null;
      const withCity = await supabase.rpc('admin_list_search_guides', { p_city_id: city?.id ?? null });
      if (withCity.error) {
        if (!rpcMissing(withCity.error.message, 'admin_list_search_guides')) {
          throw new Error(withCity.error.message);
        }
        const bare = await supabase.rpc('admin_list_search_guides', {});
        if (bare.error) throw new Error(bare.error.message);
        setTable(normalizeTable(bare.data));
        note =
          'Per-city venue counts are not set up yet — apply ' +
          'supabase/migrations/20260871000000_collection_admin_covers.sql and reload. The preview ' +
          'itself is exact either way: it is the same get_search_guides the app calls. Only the ' +
          '"venues in the table" column below is answered without a city until then.';
      } else {
        setTable(normalizeTable(withCity.data));
      }
      setSetupNote(note);
    } catch (e: unknown) {
      setError(errText(e, 'Could not load the preview'));
      setGuides([]);
      setTable([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await loadPreviewCities();
        if (cancelled) return;
        setCities(list);
        const stored = readStoredPreviewCity();
        const chosen = list.find((c) => c.id === stored) ?? list[0] ?? null;
        if (chosen) {
          setCityId(chosen.id);
          writeStoredPreviewCity(chosen.id);
        }
        await run(chosen);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(errText(e, 'Could not load the city list'));
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [run]);

  const selectCity = useCallback(
    (id: string) => {
      setCityId(id);
      writeStoredPreviewCity(id);
      const city = cities.find((c) => c.id === id) ?? null;
      if (!city) {
        showError('That city has no coordinates, so the reader’s city cannot be resolved');
        return;
      }
      void run(city);
    },
    [cities, run, showError],
  );

  const shownIds = useMemo(() => new Set(guides.map((g) => g.id)), [guides]);

  /** Live in `search_guides`, absent from what the reader receives. The interesting list. */
  const missing = useMemo(
    () => table.filter((t) => t.is_active && !shownIds.has(t.id)),
    [shownIds, table],
  );

  const liveCount = useMemo(() => table.filter((t) => t.is_active).length, [table]);

  /**
   * 20260869000000 promotes each collection's first UNUSED photo to position 0 so no two cards
   * show the same image. This counts any that still collide — expected 0, and if it is not 0 the
   * covers are colliding on the real search tab too.
   */
  const duplicateCovers = useMemo(() => {
    const seen = new Set<string>();
    let dupes = 0;
    guides.forEach((g) => {
      const url = g.venues[0]?.photo_url ?? '';
      if (!url) return;
      if (seen.has(url)) dupes += 1;
      else seen.add(url);
    });
    return dupes;
  }, [guides]);

  const cityName = cities.find((c) => c.id === cityId)?.name ?? null;

  return (
    <div className="admin-page !pt-6 max-w-6xl mx-auto">
      {toastNode}

      <div className="mb-5 flex flex-wrap items-center gap-3 gap-y-3">
        <label className="flex shrink-0 items-center gap-2 text-xs text-muted">
          <span className="text-mutedDim">Preview city</span>
          <select
            value={cityId}
            onChange={(e) => selectCity(e.target.value)}
            disabled={loading || cities.length === 0}
            className="admin-input !w-auto !px-2 !text-xs"
          >
            {cities.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.pool > 0 ? ` (${c.pool})` : ''}
              </option>
            ))}
          </select>
        </label>
        <span className="rounded-full border border-border/80 bg-white/[0.04] px-3 py-1 text-[11px] text-muted">
          get_search_guides({PER_GUIDE} per collection, {MIN_VENUES} minimum) — the exact call the
          app makes{resolvedCity ? `, resolved to ${resolvedCity}` : ''}.
        </span>
        <button
          type="button"
          onClick={() => void run(cities.find((c) => c.id === cityId) ?? null)}
          disabled={loading}
          className="admin-btn admin-btn-secondary admin-btn-sm ml-auto"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error ? <ErrorBanner className="mb-5">{error}</ErrorBanner> : null}
      {setupNote && !error ? (
        <div className="mb-5 max-w-3xl rounded-md border border-border/90 bg-white/[0.025] px-3 py-2 text-[11px] leading-relaxed text-muted">
          {setupNote}
        </div>
      ) : null}

      <div
        className="mb-6 grid gap-4"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}
      >
        <StatCard label="Live in the table" value={loading ? null : liveCount} sub="is_active in search_guides" />
        <StatCard
          label={`Showing in ${cityName ?? 'this city'}`}
          value={loading ? null : guides.length}
          sub="What the reader receives"
        />
        <StatCard
          label="Live but not showing"
          value={loading ? null : missing.length}
          sub={`Under the ${MIN_VENUES}-venue minimum here, or per viewer`}
        />
        <StatCard
          label="Colliding covers"
          value={loading ? null : duplicateCovers}
          sub="Two cards on the same photo — should be 0"
        />
      </div>

      {loading ? (
        <SkeletonRows rows={8} />
      ) : guides.length === 0 && !error ? (
        <EmptyState
          title="Nothing renders in this city"
          hint={`No live collection can fill ${MIN_VENUES} venues here. Pick a city with more rated venues, or turn on a worldwide collection.`}
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="admin-table">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">Cover</th>
                <th className="px-4 py-3 text-left">Collection</th>
                <th className="px-4 py-3 text-left">Kind</th>
                <th className="px-4 py-3 text-right">Venues</th>
                <th className="px-4 py-3 text-left">Then</th>
              </tr>
            </thead>
            <tbody>
              {guides.map((g, i) => {
                const cover = g.venues[0] ?? null;
                const rest = g.venues.slice(1, 4).map((v) => v.name ?? 'Untitled venue');
                return (
                  <tr
                    key={g.id || `${g.title}-${i}`}
                    className={`border-b border-border/50 ${i % 2 === 0 ? 'bg-surface' : 'bg-rowAlt'}`}
                  >
                    <td className="px-4 py-4 align-top text-sm tabular-nums text-mutedDim">{i + 1}</td>
                    <td className="px-4 py-4 align-top">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md border border-border bg-black">
                          {cover?.photo_url ? (
                            <img
                              src={cover.photo_url}
                              alt=""
                              referrerPolicy="no-referrer"
                              loading="lazy"
                              className="h-full w-full object-cover"
                            />
                          ) : null}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate text-[11px] text-muted">
                            {cover?.name ?? '—'}
                          </div>
                          {/* Repo rule: a DISPLAYED count is distinct raters. */}
                          <div className="text-[10px] tabular-nums text-mutedDim">
                            {cover?.score ?? '—'} · {cover?.rater_count ?? 0} rater
                            {cover?.rater_count === 1 ? '' : 's'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <div className="admin-label-primary">{g.title}</div>
                      {g.subtitle ? (
                        <div className="mt-0.5 text-[11px] font-normal text-mutedDim">{g.subtitle}</div>
                      ) : null}
                      {VIEWER_KINDS.has(g.kind) ? (
                        <div className="mt-1 text-[10px] text-amber-400">
                          Measured against your own account — every reader sees a different list.
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-4 align-top text-sm text-muted">
                      {KIND_LABELS[g.kind] ?? g.kind}
                    </td>
                    <td className="px-4 py-4 align-top text-right admin-metric tabular-nums">
                      {g.venues.length}
                    </td>
                    <td className="px-4 py-4 align-top text-[11px] leading-relaxed text-mutedDim">
                      {rest.length > 0 ? rest.join(' · ') : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && missing.length > 0 ? (
        <section className="mt-8">
          <h2 className="admin-section-label mb-3">Live, but not on this city&apos;s page</h2>
          <ul className="divide-y divide-border/50 overflow-hidden rounded-xl border border-border">
            {missing.map((m) => {
              const viewerScoped = VIEWER_KINDS.has(m.kind);
              return (
                <li key={m.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 bg-surface px-4 py-3">
                  <span className="admin-label-primary">{m.title}</span>
                  <span className="text-[11px] text-mutedDim">{KIND_LABELS[m.kind] ?? m.kind}</span>
                  <span className="ml-auto text-[11px] text-muted">
                    {viewerScoped ? (
                      /* ⚠️ Not an empty collection. See the file header. */
                      <span className="text-amber-400">
                        Per viewer — reads each reader&apos;s own follows, so it cannot be measured
                        from here. Absent does not mean off.
                      </span>
                    ) : (
                      <>
                        {m.venue_count} venue{m.venue_count === 1 ? '' : 's'} here — under the{' '}
                        {MIN_VENUES} the app needs, so it is dropped rather than shown short.
                      </>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

export function SearchPageScreen() {
  const [section, setSection] = useState<Section>(readStoredSection);

  const pick = useCallback((s: Section) => {
    setSection(s);
    writeStoredSection(s);
  }, []);

  return (
    <div>
      {/* Header + strip only; each section brings its own padded page body below. */}
      <div className="admin-page !pb-0 max-w-6xl mx-auto">
        <PageHeader
          title="Search page"
          subtitle="Spotlight — the announcement, poll or question you write yourself, its Tonight and Board switches, and everything people have posted on a board — plus the collections underneath it: what exists, what is live, and what a reader in one city actually receives."
        />
        <div className="flex items-center gap-2">
          {/*
            ⚠️ FIRST, BIGGER, AND FILLED — not a fourth pill in a row of four lookalikes.
            Evan, 2026-09-19: *"I don't see where I can mess with the spotlight from my admin
            dashboard."* It was the last of four identically-styled pills on a screen that opened on
            Collections, which is an excellent way to hide something. This one overrides the
            `.admin-pill` height and fill so it reads as the button it is, and the dot is there so
            it is findable by shape at a glance rather than by reading four labels.
          */}
          <button
            type="button"
            className="admin-pill !h-8 !px-3.5 !text-[12px] !font-semibold !border-accent !text-textPrimary"
            style={{ background: section === 'spotlight' ? 'rgba(139,92,246,0.22)' : 'rgba(139,92,246,0.10)' }}
            data-active={section === 'spotlight'}
            onClick={() => pick('spotlight')}
          >
            <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-accent align-middle" />
            Spotlight
          </button>
          <button
            type="button"
            className="admin-pill"
            data-active={section === 'collections'}
            onClick={() => pick('collections')}
          >
            Collections
          </button>
          <button
            type="button"
            className="admin-pill"
            data-active={section === 'preview'}
            onClick={() => pick('preview')}
          >
            Live preview
          </button>
          {/* Proposing a collection and switching one live are the same job, and splitting them
              across two sidebar entries would hide the count behind a navigation. */}
          <button
            type="button"
            className="admin-pill"
            data-active={section === 'assistant'}
            onClick={() => pick('assistant')}
          >
            New collection
          </button>
        </div>
      </div>

      {section === 'spotlight' ? (
        <SpotlightPanel />
      ) : section === 'collections' ? (
        <CollectionsScreen />
      ) : section === 'preview' ? (
        <LivePreviewSection />
      ) : (
        <CollectionAssistantPanel />
      )}
    </div>
  );
}
